import { useReducer, useEffect, useMemo, useCallback } from "react";
import type { ReactNode } from "react";
import type {
  AuthContextType,
  AuthState,
  LoginCredentials,
  RegisterCredentials,
  AuthTokens,
  User,
  BuyerRegisterCredentials,
} from "../types";
// import type { User } from "../types/user";
import { authService } from "../services/auth";
import { authUtils, tokenUtilsV2, userUtils } from "../utils/auth";
import { AuthContext } from "./AuthContextDef";
import { identifyUserMixpanel, trackEvent } from "@/utils/analytics";
import { useCartStore } from "@/stores/cartStore";

// Initial state
const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
type AuthAction =
  | { type: "AUTH_START" }
  | { type: "AUTH_SUCCESS"; payload: { user: User; tokens: AuthTokens } }
  | { type: "AUTH_FAILURE"; payload: string }
  | { type: "AUTH_LOGOUT" }
  | { type: "CLEAR_ERROR" }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "UPDATE_USER"; payload: User };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "AUTH_START":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "AUTH_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case "AUTH_FAILURE":
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case "AUTH_LOGOUT":
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    case "UPDATE_USER":
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

// Provider component
type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const { clearCartOnLogout } = useCartStore();

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { tokens, isAuthenticated } = authUtils.getAuthState();

        if (isAuthenticated && tokens) {
          // Get cached user data as fallback
          const cachedUser = userUtils.getUser();

          // Check if token is expired before making API call
          if (tokenUtilsV2.isTokenExpired(tokens.accessToken)) {
            console.warn("Access token is expired, clearing auth state");
            authUtils.clearAuth();
            dispatch({ type: "AUTH_LOGOUT" });
            return;
          }

          // Try to fetch fresh user data from the server
          try {
            const currentUser = await authService.getBuyerProfile();

            // Update stored user data with fresh profile
            userUtils.storeUser(currentUser);

            dispatch({
              type: "AUTH_SUCCESS",
              payload: { user: currentUser, tokens },
            });
          } catch (error) {
            console.error("Failed to fetch user profile:", error);
            
            // Check if this is an authentication error (401)
            const isAuthError = error && 
              typeof error === 'object' && 
              'response' in error && 
              error.response && 
              typeof error.response === 'object' &&
              'status' in error.response && 
              error.response.status === 401;

            if (isAuthError) {
              // Token is invalid or expired, clear auth state
              console.warn("Authentication error (401), logging out");
              authUtils.clearAuth();
              dispatch({ type: "AUTH_LOGOUT" });
            } else {
              // For other errors (network, server errors), use cached user data
              if (cachedUser) {
                console.warn("Using cached user data due to API error");
                dispatch({
                  type: "AUTH_SUCCESS",
                  payload: { user: cachedUser, tokens },
                });
              } else {
                // No cached data available, must logout
                console.error("No cached user data available, logging out");
                authUtils.clearAuth();
                dispatch({ type: "AUTH_LOGOUT" });
              }
            }
          }
        } else {
          // No valid authentication found
          dispatch({ type: "SET_LOADING", payload: false });
        }
      } catch (error) {
        console.error("Failed to initialize auth:", error);
        authUtils.clearAuth();
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    initializeAuth();
  }, []);



  // Login function - uses buyer login endpoint
  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: "AUTH_START" });
      const response = await authService.loginBuyer(credentials);

      // Validate response data
      if (!response || !response.tokens) {
        console.error("Invalid login response:", response);
        throw new Error("Invalid response from server");
      }

      const { tokens } = response;





      // Store tokens
      tokenUtilsV2.storeTokens({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken || tokens.accessToken,
      });

      // Fetch complete buyer profile with all details
      const userProfile = await authService.getBuyerProfile();

      // Store user profile
      userUtils.storeUser(userProfile);

      const userData = {
        id: userProfile?._id,
        email: userProfile?.email,
        name: userProfile?.firstName + " " + userProfile?.lastName,
        username: userProfile?.username,
        country: userProfile?.country,
        isActive: userProfile?.isActive,
        isEmailVerified: userProfile?.emailVerified,
        isPhoneVerified: userProfile?.phoneVerified,
        isDisabled: userProfile?.isDisabled,
        isNewBuyer: userProfile?.isNewBuyer,
        isIdentityVerified: userProfile?.isIdentityVerified,
        notificationPreferences: userProfile?.notificationPreferences,
        wallet: userProfile?.wallet?.balance,
        shippingStreets: userProfile?.shippingAddresses
          ?.map(address => address?.addressLine1)
          .filter(Boolean)
          .join(', '),
        shippingCities: userProfile?.shippingAddresses
          ?.map(address => address?.city)
          .filter(Boolean)
          .join(', '),
        shippingStates: userProfile?.shippingAddresses
          ?.map(address => address?.state)
          .filter(Boolean)
          .join(', '),
        shippingZipCodes: userProfile?.shippingAddresses
          ?.map(address => address?.zipCode)
          .filter(Boolean)
          .join(', '),
        shippingCountries: userProfile?.shippingAddresses
          ?.map(address => address?.country)
          .filter(Boolean)
          .join(', '),
        createdAt: userProfile?.createdAt,
        followersCount: userProfile?.followersCount,
        followingCount: userProfile?.followingCount,
        postsCount: userProfile?.postsCount,
        profilePictureUrl: userProfile?.profilePictureUrl,
        bio: userProfile?.bio,
        phoneNumber: userProfile?.phoneNumber,
        dateOfBirth: userProfile?.dateOfBirth,
        gender: userProfile?.gender,
        source: "auqli web shop"
      }

      await identifyUserMixpanel(userData?.id, userData);
      await trackEvent("login", userData);

      dispatch({
        type: "AUTH_SUCCESS",
        payload: { user: userProfile, tokens },
      });
    } catch (error) {
      console.error("Login error:", error);
      authUtils.clearAuth();
      const errorMessage =
        error instanceof Error ? error.message : "Login failed";
      dispatch({ type: "AUTH_FAILURE", payload: errorMessage });
      throw error;
    }
  }, []);

  // Register function - uses buyer register endpoint
  const register = useCallback(
    async (credentials: RegisterCredentials | BuyerRegisterCredentials) => {
      try {
        dispatch({ type: "AUTH_START" });
        const response = await authService.registerBuyer(
          credentials as BuyerRegisterCredentials
        );

        // Validate response data
        if (!response || !response.tokens) {
          console.error("Invalid registration response:", response);
          throw new Error("Invalid response from server");
        }

        const { tokens } = response;

        // Store tokens
        tokenUtilsV2.storeTokens({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken || tokens.accessToken,
        });

        // Fetch complete buyer profile after registration
        const userProfile = await authService.getBuyerProfile();

        // Store user profile
        userUtils.storeUser(userProfile);

        // Identify user in Mixpanel
        const userData = {
          id: userProfile?._id,
          email: userProfile?.email,
          name: userProfile?.firstName + " " + userProfile?.lastName,
          username: userProfile?.username,
          country: userProfile?.country,
          isActive: userProfile?.isActive,
          isEmailVerified: userProfile?.emailVerified,
          isPhoneVerified: userProfile?.phoneVerified,
          isDisabled: userProfile?.isDisabled,
          isNewBuyer: userProfile?.isNewBuyer,
          isIdentityVerified: userProfile?.isIdentityVerified,
          notificationPreferences: userProfile?.notificationPreferences,
          wallet: userProfile?.wallet?.balance,
          shippingStreets: userProfile?.shippingAddresses
            ?.map(address => address?.addressLine1)
            .filter(Boolean)
            .join(', '),
          shippingCities: userProfile?.shippingAddresses
            ?.map(address => address?.city)
            .filter(Boolean)
            .join(', '),
          shippingStates: userProfile?.shippingAddresses
            ?.map(address => address?.state)
            .filter(Boolean)
            .join(', '),
          shippingZipCodes: userProfile?.shippingAddresses
            ?.map(address => address?.zipCode)
            .filter(Boolean)
            .join(', '),
          shippingCountries: userProfile?.shippingAddresses
            ?.map(address => address?.country)
            .filter(Boolean)
            .join(', '),
          createdAt: userProfile?.createdAt,
          followersCount: userProfile?.followersCount,
          followingCount: userProfile?.followingCount,
          postsCount: userProfile?.postsCount,
          profilePictureUrl: userProfile?.profilePictureUrl,
          bio: userProfile?.bio,
          phoneNumber: userProfile?.phoneNumber,
          dateOfBirth: userProfile?.dateOfBirth,
          gender: userProfile?.gender,
          source: "auqli web shop"
        }

        await identifyUserMixpanel(userData?.id, userData);
        await trackEvent("signup_completed", userData);

        dispatch({
          type: "AUTH_SUCCESS",
          payload: { user: userProfile, tokens },
        });
      } catch (error) {
        console.error("Registration error:", error);
        authUtils.clearAuth();
        const errorMessage =
          error instanceof Error ? error.message : "Registration failed";
        dispatch({ type: "AUTH_FAILURE", payload: errorMessage });
        throw error;
      }
    },
    []
  );

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Attempt to notify the server about logout
      await authService.logout();

      clearCartOnLogout(); // Clear cart on logout
      const userProfile = userUtils.getUser();
      const userData = {
        id: userProfile?._id,
        email: userProfile?.email,
        name: userProfile?.firstName + " " + userProfile?.lastName,
        username: userProfile?.username,
        country: userProfile?.country,
        isActive: userProfile?.isActive,
        isEmailVerified: userProfile?.emailVerified,
        isPhoneVerified: userProfile?.phoneVerified,
        isDisabled: userProfile?.isDisabled,
        isNewBuyer: userProfile?.isNewBuyer,
        isIdentityVerified: userProfile?.isIdentityVerified,
        notificationPreferences: userProfile?.notificationPreferences,
        wallet: userProfile?.wallet?.balance,
        shippingStreets: userProfile?.shippingAddresses
          ?.map(address => address?.addressLine1)
          .filter(Boolean)
          .join(', '),
        shippingCities: userProfile?.shippingAddresses
          ?.map(address => address?.city)
          .filter(Boolean)
          .join(', '),
        shippingStates: userProfile?.shippingAddresses
          ?.map(address => address?.state)
          .filter(Boolean)
          .join(', '),
        shippingZipCodes: userProfile?.shippingAddresses
          ?.map(address => address?.zipCode)
          .filter(Boolean)
          .join(', '),
        shippingCountries: userProfile?.shippingAddresses
          ?.map(address => address?.country)
          .filter(Boolean)
          .join(', '),
        createdAt: userProfile?.createdAt,
        followersCount: userProfile?.followersCount,
        followingCount: userProfile?.followingCount,
        postsCount: userProfile?.postsCount,
        profilePictureUrl: userProfile?.profilePictureUrl,
        bio: userProfile?.bio,
        phoneNumber: userProfile?.phoneNumber,
        dateOfBirth: userProfile?.dateOfBirth,
        gender: userProfile?.gender,
        source: "auqli web shop"
      }
      await trackEvent("logout", userData);
    } catch (error) {
      console.warn("Logout request failed:", error);
      // Continue with local logout even if server request fails
    } finally {
      // Always clear local storage regardless of API call success
      authUtils.clearAuth();
      dispatch({ type: "AUTH_LOGOUT" });
    }
  }, []);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    try {
      const currentRefreshToken = tokenUtilsV2.getRefreshToken();
      if (!currentRefreshToken) {
        throw new Error("No refresh token available");
      }

      const tokens = await authService.refreshToken(currentRefreshToken);

      // Store new tokens
      tokenUtilsV2.storeTokens(tokens);

      // Update state with new tokens
      if (state.user) {
        dispatch({
          type: "AUTH_SUCCESS",
          payload: { user: state.user, tokens },
        });
      }

      return tokens;
    } catch (error) {
      console.error("Token refresh failed:", error);
      // Clear auth state if refresh fails
      authUtils.clearAuth();
      dispatch({ type: "AUTH_LOGOUT" });
      throw error;
    }
  }, [state.user]);

  // Refresh user profile - useful for updating user data after profile changes
  const refreshUserProfile = useCallback(async () => {
    try {
      const userProfile = await authService.getBuyerProfile();

      // Update stored user data
      userUtils.storeUser(userProfile);

      // Update state
      dispatch({ type: "UPDATE_USER", payload: userProfile });

      return userProfile;
    } catch (error) {
      console.error("Failed to refresh user profile:", error);
      throw error;
    }
  }, []);

  // Sync auth state from localStorage - useful after OAuth login
  const syncAuthState = useCallback(async () => {
    try {
      const accessToken = tokenUtilsV2.getAccessToken();
      const refreshToken = tokenUtilsV2.getRefreshToken();
      const cachedUser = userUtils.getUser();
      
      if (!accessToken || !cachedUser) {
        throw new Error("No auth data in storage");
      }
      
      const tokens: AuthTokens = {
        accessToken,
        refreshToken: refreshToken || undefined,
      };
      
      // Fetch fresh user profile
      const userProfile = await authService.getBuyerProfile();
      
      // Update stored user data
      userUtils.storeUser(userProfile);
      
      // Dispatch AUTH_SUCCESS to update all state fields including isAuthenticated
      dispatch({
        type: "AUTH_SUCCESS",
        payload: { user: userProfile, tokens },
      });
      
      return userProfile;
    } catch (error) {
      console.error("Failed to sync auth state:", error);
      throw error;
    }
  }, []);

  // Update user profile
  const updateUserProfile = useCallback(async (userData: Partial<User>) => {
    try {
      const updatedUser = await authService.updateBuyerProfile(userData);

      // Update stored user data
      userUtils.storeUser(updatedUser);

      // Update state
      dispatch({ type: "UPDATE_USER", payload: updatedUser });

      return updatedUser;
    } catch (error) {
      console.error("Failed to update user profile:", error);
      throw error;
    }
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    dispatch({ type: "CLEAR_ERROR" });
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextType>(
    () => ({
      ...state,
      login,
      register,
      logout,
      refreshToken,
      refreshUserProfile,
      syncAuthState,
      updateUserProfile,
      clearError,
    }),
    [
      state,
      login,
      register,
      logout,
      refreshToken,
      refreshUserProfile,
      syncAuthState,
      updateUserProfile,
      clearError,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
